import { CheckCircleIcon } from '@heroicons/react/20/solid'
import type { ComponentProps, ReactElement } from 'react'

import type { AppTestCaseGroup } from '../../common/components/ApplicationConfigPage/types'
import { Badge } from '../../common/designs'

interface Props {
  application: AppTestCaseGroup
}

type BadgeVariant = ComponentProps<typeof Badge>['variant']

interface StatusBadgeProps {
  variant: BadgeVariant
  title: string
  label: string
  className?: string
  rounded?: boolean
  icon?: ReactElement
}

const StatusBadge = ({ variant, title, label, className, rounded, icon }: StatusBadgeProps) => (
  <span className='inline-block max-w-full' title={title}>
    <Badge
      variant={variant}
      size='s'
      {...(className ? { className } : {})}
      {...(rounded !== undefined ? { rounded } : {})}
      {...(icon ? { icon } : {})}
    >
      {label}
    </Badge>
  </span>
)

const ApplicationRowStatus = ({ application }: Props) => {
  if (application.hasFailingTests) {
    return (
      <StatusBadge
        variant='red'
        title='One or more config tests are failing for this application.'
        label='Failing tests'
        className='max-w-full truncate'
      />
    )
  }

  if (application.hasNotRunTests) {
    return (
      <StatusBadge
        variant='orange'
        title='Rules or tests changed since the last test run.'
        label='Tests not run'
        className='max-w-full truncate'
      />
    )
  }

  if (application.hasNoDataCollectionRules) {
    return (
      <StatusBadge
        variant='gray'
        title='No data collection rules are enabled for this application.'
        label='Not configured'
        className='max-w-full truncate'
      />
    )
  }

  return (
    <StatusBadge
      variant='green'
      title='Rules and tests look healthy for this application.'
      label='Configured'
      rounded
      icon={<CheckCircleIcon aria-hidden />}
      className='max-w-full truncate border border-emerald-600/25 bg-emerald-50 text-emerald-900 shadow-sm'
    />
  )
}

export default ApplicationRowStatus
